import React, { useRef, useState } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
const CardSection = () => {
  const cardTextRef = useRef(null);
  // Data for cards
  const cardData = [
    {
      title: "Fee Calculation",
      desc: "Get a clear estimate of your study expenses with our fee calculation service. We break down tuition fees, living costs, and other expenses to help you plan your budget.",
      img: "/Assets/Fee-Calculation.png"
    },
    {
      title: "University Selection and Applications",
      desc: "Choosing the right university is crucial for your future. We guide you in selecting the best-fit institutions and assist with application submission to maximize your chances of acceptance.",
      img: "/Assets/University-Selection-and-Applications.png"
    },
    {
      title: "Visa and Documentation Assistance",
      desc: "Navigating visa requirements can be challenging, but we make it simple. From document preparation to interview coaching, we ensure you meet all visa requirements for a smooth approval process.",
      img: "/Assets/Visa-and-Documentation-Assistance.png"
    },
    {
      title: "Bank Account Setup",
      desc: "Opening a bank account abroad is essential for managing your finances. We help you understand the process, required documents, and the best banking options for international students.",
      img: "/Assets/Bank-Account-Setup.png"
    },
    {
      title: "Accommodation Support",
      desc: "Finding a safe and affordable place to live is a top priority. We assist in securing student-friendly accommodations, whether it's on-campus housing, private rentals, or shared apartments.",
      img: "/Assets/Accommodation-Support.png"
    },
    {
      title: "Health Insurance Guidance",
      desc: "Health coverage is a must when studying abroad. We guide you in selecting the right health insurance plan to meet university and visa requirements while ensuring your well-being.",
      img: "/Assets/Health-Insurance-Guidance.png"
    }
  ];

  // State for active card
  const [activeIndex, setActiveIndex] = useState(0);
  const activeIndicatorRef = useRef(null);
  const cardWrapperRef = useRef(null);

  // Animation for the heading
  useGSAP(() => {
    // Register GSAP plugins
    gsap.registerPlugin(ScrollTrigger);

    // GSAP scroll-triggered animation for .line elements
    gsap.from(cardTextRef.current, {
      y: 100,         // Initial Y offset
      opacity: 0,     // Start with opacity 0
      duration: 1.5,    // Duration of the animation
      stagger: 0.1,   // Stagger delay between elements
      ease: "power3.out", // Easing for smoothness
      scrollTrigger: {
        trigger: "#card-section",  // Trigger animation when this element comes into view
        start: "top 80%",  // Start when top of the section is at 80% of viewport height
      }
    });
  }, []);

  // Update card content
  const updateContent = (index) => {
    setActiveIndex(index);
    
    // GSAP animation for the indicator
    gsap.to(activeIndicatorRef.current, {
      y: index * 70,
      duration: 0.3,
      ease: "power2.out"
    });

    // Background image change animation
    gsap.to(cardWrapperRef.current, {
      backgroundImage: `url('${cardData[index].img}')`,
      duration: 0.5,
      ease: "power2.inOut"
    });
  };

  return (
    <section id='card-section' className="relative w-full min-h-screen flex flex-col items-center justify-center bg-[#cac8bb] px-0 md:px-28 p-10">
      {/* Heading */}
      <div className="animate-lines relative mx-auto mb-[400px] mt-44 overflow-hidden">
        <h2 ref={cardTextRef} id='study-abroad' className="text-[110px] font-bold text-black leading-[1] tracking-tighter flex flex-col items-start justify-center">
          <div className="">SEAMLESS STUDY</div>
          <div className=""><span className="text-[#457b9d]">ABROAD EXPERIENCE</span></div>
        </h2>
        <p className="text-[24px] text-gray-800 mt-8 mb-8 tracking-tight ">
          Let us handle the details while you focus on your academic goals. Our expert services <br /> make your journey stress-free and efficient.
        </p>
      </div>
      
      {/* Main Card */}
      <div 
        ref={cardWrapperRef}
        id="card-wrapper" 
        className="absolute inset-x-auto top-[65%] flex w-[75%] h-[600px] rounded-3xl overflow-hidden bg-cover bg-center shadow-lg transition-all duration-500"
        style={{ backgroundImage: `url('${cardData[activeIndex].img}')` }}
      >
        {/* Left: Sidebar Navigation */}
        <div className="w-1/2 text-black p-8 flex flex-col gap-6 relative">
          <div className="absolute left-32 top-[22%] bottom-[10%] w-1 bg-white rounded">
            <div 
              ref={activeIndicatorRef}
              className="h-[65px] w-1 bg-[#457b9d] transition-all duration-300" 
              id="active-indicator"
            />
          </div>
          {cardData.map((item, index) => (
            <button 
              key={index}
              className={`ml-32 mt-4 text-white text-left transition-all hover:text-[#457b9d] ${index === 0 ? 'mt-[110px]' : ''}`}
              onClick={() => updateContent(index)}
            >
              {item.title}
            </button>
          ))}
        </div>

        {/* Right: Info Card */}
        <div className="relative w-3/4 h-full">
          {/* Blue Card */}
          <div className="absolute bottom-0 right-28 bg-[#457b9d] p-12 rounded-t-3xl max-w-xl shadow-md">
            <h3 className="text-white font-bold text-normal mt-2">
              {cardData[activeIndex].title}
            </h3>
            <p className="text-white mt-8 mb-4 text-normal">
              {cardData[activeIndex].desc}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CardSection;